import type { IIOStream } from "./types.ts";

// MediaPipe hand landmark indices
const WRIST = 0;
const INDEX_MCP = 5; // Base of index finger
const PINKY_MCP = 17; // Base of pinky finger
const MIDDLE_MCP = 9; // Middle finger base

interface Landmark {
  x: number;
  y: number;
  z?: number;
}

interface HandsData {
  landmarks?: Landmark[][];
  worldLandmarks?: Landmark[][];
}

interface RotationResults {
  hands?: HandsData;
  pose?: any;
  rotationControlType?: string;
  angle?: number;
  normal?: [number, number, number];
  normal1?: [number, number, number];
  normal2?: [number, number, number];
  handIndex?: number; // Which hand (0 = left, 1 = right)
}

interface RenderRotationControlParams {
  canvasCtx: CanvasRenderingContext2D;
  rotationResults: RotationResults;
  handsVisible: boolean[];
  canvasAlreadyMirrored?: boolean; // Whether the canvas context is already horizontally mirrored
}

// Helper: Calculate 3D vector from point A to point B
function vector3D(a: Landmark, b: Landmark): [number, number, number] {
  return [b.x - a.x, b.y - a.y, (b.z || 0) - (a.z || 0)];
}

// Helper: Cross product of two 3D vectors
function crossProduct(
  v1: [number, number, number],
  v2: [number, number, number],
): [number, number, number] {
  return [
    v1[1] * v2[2] - v1[2] * v2[1],
    v1[2] * v2[0] - v1[0] * v2[2],
    v1[0] * v2[1] - v1[1] * v2[0],
  ];
}

// Helper: Dot product of two 3D vectors
function dotProduct(
  v1: [number, number, number],
  v2: [number, number, number],
): number {
  return v1[0] * v2[0] + v1[1] * v2[1] + v1[2] * v2[2];
}

// Helper: Magnitude of a 3D vector
function magnitude(v: [number, number, number]): number {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
}

// Helper: Normalize a 3D vector
function normalize(v: [number, number, number]): [number, number, number] {
  const mag = magnitude(v);
  if (mag === 0) return [0, 0, 0];
  return [v[0] / mag, v[1] / mag, v[2] / mag];
}

// Calculate palm normal from hand world landmarks
function calculatePalmNormal(
  handWorldLandmarks: Landmark[],
): [number, number, number] {
  const wrist = handWorldLandmarks[WRIST];
  const indexMcp = handWorldLandmarks[INDEX_MCP];
  const pinkyMcp = handWorldLandmarks[PINKY_MCP];

  // Create two vectors in the palm plane
  const v1 = vector3D(wrist, indexMcp);
  const v2 = vector3D(wrist, pinkyMcp);

  // Cross product gives the normal to the plane
  const normal = crossProduct(v1, v2);
  return normalize(normal);
}

// Calculate angle between two vectors in degrees
function angleBetweenVectors(
  v1: [number, number, number],
  v2: [number, number, number],
): number {
  const dot = dotProduct(v1, v2);
  const mag1 = magnitude(v1);
  const mag2 = magnitude(v2);

  if (mag1 === 0 || mag2 === 0) return 0;

  // Clamp to [-1, 1] to handle floating point errors
  const cosAngle = Math.max(-1, Math.min(1, dot / (mag1 * mag2)));
  const angleRad = Math.acos(cosAngle);
  return (angleRad * 180) / Math.PI; // Convert to degrees
}

// Calculate euler angles from a normal vector (simple approximation)
function normalToEulerAngles(
  normal: [number, number, number],
): { pitch: number; yaw: number; roll: number } {
  const [nx, ny, nz] = normal;

  // Pitch: rotation around X axis (up/down tilt)
  const pitch = Math.atan2(-ny, Math.sqrt(nx * nx + nz * nz)); // * (180 / Math.PI);

  // Yaw: rotation around Y axis (left/right turn)
  const yaw = Math.atan2(nx, nz); // * (180 / Math.PI);

  // Roll: we don't have enough info for true roll, so use 0
  const roll = 0;

  return { pitch, yaw, roll };
}

// Calculate 2D angle (in degrees) from one point to another using only x,y
function calculate2DAngle(
  from: Landmark,
  to: Landmark,
): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;

  // atan2 returns radians, convert to degrees
  const angleRad = Math.atan2(dy, dx);
  return (angleRad * 180) / Math.PI;
}

// Calculate palm center from hand landmarks
function calculatePalmCenter(handLandmarks: Landmark[]): Landmark {
  const wrist = handLandmarks[WRIST];
  const indexMcp = handLandmarks[INDEX_MCP];
  const pinkyMcp = handLandmarks[PINKY_MCP];

  return {
    x: (wrist.x + indexMcp.x + pinkyMcp.x) / 3,
    y: (wrist.y + indexMcp.y + pinkyMcp.y) / 3,
    z: ((wrist.z || 0) + (indexMcp.z || 0) + (pinkyMcp.z || 0)) / 3,
  };
}

// Render 3D rotation arrows on canvas
export const renderRotationControl = (
  { canvasCtx, rotationResults, handsVisible }: RenderRotationControlParams,
): void => {
  const { hands, rotationControlType } = rotationResults;

  if (!hands?.landmarks || !hands.landmarks.length) return;

  const flip = globalThis.options?.cameraFlipHorizontal;

  if (rotationControlType === "single-hand-palm-orientation") {
    // Draw arrow showing palm normal direction for single hand
    const hand = hands.landmarks[0];
    const isLeftHandVisible = handsVisible[0];

    // Calculate center of palm in 2D
    const palmCenter = {
      x: (hand[0].x + hand[5].x + hand[17].x) / 3,
      y: (hand[0].y + hand[5].y + hand[17].y) / 3,
    };

    // Get the 3D normal if available from rotationResults
    const normal = rotationResults.normal;

    // Draw arrow from palm center in normal direction
    const arrowLength = 150; // pixels

    canvasCtx.strokeStyle = "#87CEEB"; // Light blue
    canvasCtx.fillStyle = "#87CEEB";
    canvasCtx.lineWidth = 5;

    // Calculate X coordinate: flip if canvas is NOT already mirrored
    const startX = palmCenter.x * canvasCtx.canvas.width;
    const startY = palmCenter.y * canvasCtx.canvas.height;

    // Project 3D normal to 2D screen space
    // Use X and Y components of the normal for 2D direction
    let endX = startX;
    let endY = startY;

    if (normal) {
      // If canvas is already mirrored, we need to flip the X direction of the normal
      const normalX = -normal[0];
      const xMultiplier = flip
        ? isLeftHandVisible ? 1 : -1
        : isLeftHandVisible
        ? -1
        : 1; // Flip X for right hand only
      const yMultiplier = flip
        ? isLeftHandVisible ? -1 : 1
        : isLeftHandVisible
        ? 1
        : -1; // Flip Y for left hand only
      // Project the normal: use X for horizontal, Y for vertical
      endX = startX + normalX * arrowLength * xMultiplier;
      endY = startY + normal[1] * arrowLength * yMultiplier;
    }

    // Draw the arrow line
    canvasCtx.beginPath();
    canvasCtx.moveTo(startX, startY);
    canvasCtx.lineTo(endX, endY);
    canvasCtx.stroke();

    // Draw arrowhead
    const headLength = 20;
    const angle = Math.atan2(endY - startY, endX - startX);

    canvasCtx.beginPath();
    canvasCtx.moveTo(endX, endY);
    canvasCtx.lineTo(
      endX - headLength * Math.cos(angle - Math.PI / 6),
      endY - headLength * Math.sin(angle - Math.PI / 6),
    );
    canvasCtx.lineTo(
      endX - headLength * Math.cos(angle + Math.PI / 6),
      endY - headLength * Math.sin(angle + Math.PI / 6),
    );
    canvasCtx.closePath();
    canvasCtx.fill();

    // Draw circle at palm center
    canvasCtx.beginPath();
    canvasCtx.arc(startX, startY, 8, 0, 2 * Math.PI);
    canvasCtx.fill();
  } else if (rotationControlType === "two-hand-palm-angle") {
    // Draw lines between palms showing the angle
    if (hands.landmarks.length >= 2) {
      const hand1 = hands.landmarks[0];
      const hand2 = hands.landmarks[1];

      // Calculate palm centers
      const palm1Center = {
        x: (hand1[0].x + hand1[5].x + hand1[17].x) / 3,
        y: (hand1[0].y + hand1[5].y + hand1[17].y) / 3,
      };

      const palm2Center = {
        x: (hand2[0].x + hand2[5].x + hand2[17].x) / 3,
        y: (hand2[0].y + hand2[5].y + hand2[17].y) / 3,
      };

      // Calculate X coordinates: flip if canvas is NOT already mirrored
      const palm1X = palm1Center.x * canvasCtx.canvas.width;
      const palm1Y = palm1Center.y * canvasCtx.canvas.height;
      const palm2X = palm2Center.x * canvasCtx.canvas.width;
      const palm2Y = palm2Center.y * canvasCtx.canvas.height;

      // Draw line connecting palm centers
      canvasCtx.strokeStyle = "#87CEEB"; // Light blue
      canvasCtx.lineWidth = 3;
      canvasCtx.setLineDash([10, 5]);

      canvasCtx.beginPath();
      canvasCtx.moveTo(palm1X, palm1Y);
      canvasCtx.lineTo(palm2X, palm2Y);
      canvasCtx.stroke();

      canvasCtx.setLineDash([]);

      // Draw circles at palm centers
      canvasCtx.fillStyle = "#87CEEB"; // Light blue
      canvasCtx.beginPath();
      canvasCtx.arc(palm1X, palm1Y, 8, 0, 2 * Math.PI);
      canvasCtx.fill();

      canvasCtx.beginPath();
      canvasCtx.arc(palm2X, palm2Y, 8, 0, 2 * Math.PI);
      canvasCtx.fill();
    }
  }
};

interface ClenchData {
  isClenchLeft?: boolean;
  isClenchRight?: boolean;
}

interface RotationInputs {
  hands?: HandsData;
  pose?: any;
  clench?: ClenchData;
}

interface RotationOutput {
  hands: HandsData;
  pose?: any;
  rotationControlType: string;
  angle?: number;
  normal?: [number, number, number];
  normal1?: [number, number, number];
  normal2?: [number, number, number];
  handIndex?: number;
  pitch?: number;
  yaw?: number;
  roll?: number;
}

interface GenerateRotationControllerParams {
  iostream: IIOStream;
}

export interface RotationController {
  dispose: () => void;
  onInputs: (inputs: RotationInputs) => RotationOutput | undefined;
  root: HTMLDivElement;
}

export const generateRotationController = ({
  iostream,
}: GenerateRotationControllerParams): RotationController => {
  if (iostream === undefined) {
    throw new Error("iostream is required");
  }

  let cancelled = false;
  const root = document.createElement("div");

  // Position in top right corner, below scalar control
  root.style.position = "absolute";
  root.style.top = "4rem"; // Below scalar control (1rem + 3rem spacing)
  root.style.right = "1rem";
  root.style.width = "200px";
  root.style.height = "200px";
  root.style.padding = "0.75rem";
  root.style.zIndex = "1000";

  // Initially hide the controller
  root.style.opacity = "0";
  root.style.visibility = "hidden";
  root.style.transition = "opacity 0.3s, visibility 0.3s";

  // Create canvas for 3D arrow visualization
  const canvas = document.createElement("canvas");
  canvas.width = 200;
  canvas.height = 200;
  canvas.style.width = "100%";
  canvas.style.height = "100%";

  const ctx = canvas.getContext("2d")!;

  root.appendChild(canvas);

  // Function to draw 3D arrow based on normal vector
  function draw3DArrow(normal: [number, number, number]) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const arrowLength = 80;

    // Project 3D normal to 2D, flip X axis
    const endX = centerX - normal[0] * arrowLength;
    const endY = centerY + normal[1] * arrowLength;

    // Draw arrow shaft
    ctx.strokeStyle = "#87CEEB";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    // Draw arrowhead
    const headLength = 15;
    const angle = Math.atan2(endY - centerY, endX - centerX);

    ctx.fillStyle = "#87CEEB";
    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - headLength * Math.cos(angle - Math.PI / 6),
      endY - headLength * Math.sin(angle - Math.PI / 6),
    );
    ctx.lineTo(
      endX - headLength * Math.cos(angle + Math.PI / 6),
      endY - headLength * Math.sin(angle + Math.PI / 6),
    );
    ctx.closePath();
    ctx.fill();

    // Draw center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, 6, 0, 2 * Math.PI);
    ctx.fill();
  }

  // Handle inputs {hands, pose, clench}
  const onInputs = (inputs: RotationInputs): RotationOutput | undefined => {
    if (cancelled) return;

    const { hands, clench } = inputs;
    if (
      !hands?.worldLandmarks || !hands.worldLandmarks.length
    ) {
      root.style.opacity = "0";
      root.style.visibility = "hidden";
      return undefined;
    }

    // Check if any hand is clenched
    const isLeftClenched = clench?.isClenchLeft;
    const isRightClenched = clench?.isClenchRight;

    let visibleHands: { index: number; landmarks: Landmark[] }[] = [];
    for (let i = 0; i < hands.worldLandmarks.length; i++) {
      const isClenched = (i === 0 && isLeftClenched) ||
        (i === 1 && isRightClenched);
      if (!isClenched) {
        visibleHands.push({
          index: i,
          landmarks: hands.worldLandmarks[i],
        });
      }
    }

    if (visibleHands.length === 0) {
      root.style.opacity = "0";
      root.style.visibility = "hidden";
      return undefined;
    }

    let rotationControlType: string | null = null;

    if (visibleHands.length === 1) {
      // Single hand: calculate palm orientation (3D normal vector)
      const hand = visibleHands[0];
      const palmNormal = calculatePalmNormal(hand.landmarks);
      const { pitch, yaw, roll } = normalToEulerAngles(palmNormal);

      rotationControlType = "single-hand-palm-orientation";

      iostream.setOutputs({
        "3dAngleEuler": palmNormal,
      });

      return {
        hands: {
          worldLandmarks: [hand.landmarks],
          landmarks: hands.landmarks
            ? [hands.landmarks[visibleHands[0].index]]
            : undefined,
        },
        rotationControlType,
        normal: palmNormal,
        handIndex: visibleHands[0].index, // Store which hand this is
        pitch,
        yaw,
        roll,
      };
    } else if (visibleHands.length >= 2) {
      // Two hands: calculate 3D and 2D angles from left hand to right hand
      const hand1 = visibleHands[0]; // Left hand
      const hand2 = visibleHands[1]; // Right hand

      // Calculate palm centers in 3D world coordinates
      const palmCenter1 = calculatePalmCenter(hand1.landmarks);
      const palmCenter2 = calculatePalmCenter(hand2.landmarks);

      // Calculate 3D vector from hand1 to hand2
      const vector3d = vector3D(palmCenter1, palmCenter2);
      // Flip X to compensate for mirrored camera view
      vector3d[0] = -vector3d[0];
      const normalizedVector3d = normalize(vector3d);

      // Calculate 3D euler angles from this vector
      const { pitch, yaw, roll } = normalToEulerAngles(normalizedVector3d);

      // Calculate 2D angle (x,y plane only) from hand1 to hand2
      const angle2d = calculate2DAngle(palmCenter1, palmCenter2);

      rotationControlType = "two-hand-palm-angle";

      // Draw 3D arrow based on direction vector from left to right hand
      // draw3DArrow(normalizedVector3d);

      // root.style.opacity = "1";
      // root.style.visibility = "visible";

      iostream.setOutputs({
        "3dAngleEuler": normalizedVector3d,
        "2dAngle": angle2d,
      });

      return {
        hands: {
          worldLandmarks: visibleHands.map((h) => h.landmarks),
          landmarks: hands.landmarks
            ? visibleHands.map((h) => hands.landmarks![h.index])
            : undefined,
        },
        rotationControlType,
        angle: angle2d,
        normal: normalizedVector3d,
        pitch,
        yaw,
        roll,
      };
    }

    return undefined;
  };

  const dispose = (): void => {
    cancelled = true;
    root.remove();
  };

  return {
    dispose,
    onInputs,
    root,
  };
};
